'use client'

import React, { useEffect, useRef, useMemo } from 'react'
import { IChartApi, ISeriesApi, UTCTimestamp, LineStyle, LineSeries } from 'lightweight-charts'
import type { 
  DoubleTopBottomPattern, 
  PatternPoint, 
  Point,
  PATTERN_COLORS
} from '../../types/drawing'
import {
  validateDoubleTopBottom,
  calculateProjectedTarget 
} from '../../types/drawing'
import { createVerticalLineData } from '../../utils/chartDataUtils'

interface DoubleTopBottomProps {
  chart: IChartApi
  pattern: DoubleTopBottomPattern
  onUpdate?: (pattern: DoubleTopBottomPattern) => void
  onDelete?: (patternId: string) => void
}

const DoubleTopBottom: React.FC<DoubleTopBottomProps> = ({
  chart,
  pattern,
  onUpdate,
  onDelete
}) => {
  // Series refs for pattern lines
  const necklineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null)
  const firstPeakLineRef = useRef<ISeriesApi<'Line'> | null>(null)
  const secondPeakLineRef = useRef<ISeriesApi<'Line'> | null>(null)
  const valleyLineRef = useRef<ISeriesApi<'Line'> | null>(null)
  const projectionLineRef = useRef<ISeriesApi<'Line'> | null>(null)

  // Pattern color based on type
  const patternColor = pattern.type === 'double-top' ? '#FF9800' : '#4CAF50' // Orange for double top, Green for double bottom

  // Validate pattern points
  const isValidPattern = useMemo(() => {
    try {
      return validateDoubleTopBottom(
        pattern.patternPoints.firstPeak,
        pattern.patternPoints.secondPeak,
        pattern.tolerance
      )
    } catch {
      return false
    }
  }, [pattern.patternPoints, pattern.tolerance])

  // Calculate projected target
  const projectedTarget = useMemo(() => {
    if (!pattern.confirmation || !isValidPattern) return null
    
    const necklinePrice = (pattern.patternPoints.neckline[0].price + pattern.patternPoints.neckline[1].price) / 2
    const peakPrice = (pattern.patternPoints.firstPeak.price + pattern.patternPoints.secondPeak.price) / 2
    const patternHeight = Math.abs(peakPrice - necklinePrice)
    
    return calculateProjectedTarget(
      pattern.type,
      patternHeight,
      necklinePrice,
      true // Double tops and bottoms are reversal patterns
    )
  }, [pattern, isValidPattern])

  // Initialize pattern drawing
  useEffect(() => {
    if (!chart || !isValidPattern) return

    // Create neckline
    necklineSeriesRef.current = chart.addSeries(LineSeries, {
      color: patternColor,
      lineWidth: 2,
      lineStyle: LineStyle.Solid,
      title: `${pattern.type} Neckline`,
      priceLineVisible: false,
      lastValueVisible: false
    })

    // Create peak/valley marker lines (vertical lines to mark extremes)
    firstPeakLineRef.current = chart.addSeries(LineSeries, {
      color: patternColor,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      title: pattern.type === 'double-top' ? 'First Peak' : 'First Valley',
      priceLineVisible: false,
      lastValueVisible: false
    })

    secondPeakLineRef.current = chart.addSeries(LineSeries, {
      color: patternColor,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      title: pattern.type === 'double-top' ? 'Second Peak' : 'Second Valley',
      priceLineVisible: false,
      lastValueVisible: false
    })

    // Create valley/peak marker line (the middle point)
    valleyLineRef.current = chart.addSeries(LineSeries, {
      color: patternColor,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      title: pattern.type === 'double-top' ? 'Valley' : 'Peak',
      priceLineVisible: false,
      lastValueVisible: false
    })

    // Create projection line if pattern is confirmed
    if (pattern.confirmation && projectedTarget) {
      projectionLineRef.current = chart.addSeries(LineSeries, {
        color: '#FFD700', // Gold color for target
        lineWidth: 2,
        lineStyle: LineStyle.Dotted,
        title: `Target: ${projectedTarget.toFixed(2)}`,
        priceLineVisible: true,
        lastValueVisible: true
      })
    }

    return () => {
      // Cleanup on unmount
      if (necklineSeriesRef.current) {
        chart.removeSeries(necklineSeriesRef.current)
        necklineSeriesRef.current = null
      }
      if (firstPeakLineRef.current) {
        chart.removeSeries(firstPeakLineRef.current)
        firstPeakLineRef.current = null
      }
      if (secondPeakLineRef.current) {
        chart.removeSeries(secondPeakLineRef.current)
        secondPeakLineRef.current = null
      }
      if (valleyLineRef.current) {
        chart.removeSeries(valleyLineRef.current)
        valleyLineRef.current = null
      }
      if (projectionLineRef.current) {
        chart.removeSeries(projectionLineRef.current)
        projectionLineRef.current = null
      }
    }
  }, [chart, isValidPattern, patternColor, pattern.confirmation, projectedTarget])

  // Update neckline data
  useEffect(() => {
    if (!necklineSeriesRef.current || !pattern.patternPoints.neckline) return

    const necklineData = [
      {
        time: pattern.patternPoints.neckline[0].time as UTCTimestamp,
        value: pattern.patternPoints.neckline[0].price
      },
      {
        time: pattern.patternPoints.neckline[1].time as UTCTimestamp,
        value: pattern.patternPoints.neckline[1].price
      }
    ]

    necklineSeriesRef.current.setData(necklineData)
  }, [pattern.patternPoints.neckline])

  // Update peak markers
  useEffect(() => {
    const valleyPrice = pattern.patternPoints.valley.price
    
    // First Peak marker - create vertical line using utility function
    if (firstPeakLineRef.current) {
      const firstPeakData = createVerticalLineData(
        pattern.patternPoints.firstPeak.time,
        valleyPrice,
        pattern.patternPoints.firstPeak.price
      )
      firstPeakLineRef.current.setData(firstPeakData)
    }

    // Second Peak marker - create vertical line using utility function
    if (secondPeakLineRef.current) {
      const secondPeakData = createVerticalLineData(
        pattern.patternPoints.secondPeak.time,
        valleyPrice,
        pattern.patternPoints.secondPeak.price
      )
      secondPeakLineRef.current.setData(secondPeakData)
    }

    // Valley marker (horizontal line connecting the valley point)
    if (valleyLineRef.current) {
      const valleyData = [
        {
          time: pattern.patternPoints.firstPeak.time as UTCTimestamp,
          value: valleyPrice
        },
        {
          time: pattern.patternPoints.secondPeak.time as UTCTimestamp,
          value: valleyPrice
        }
      ]
      valleyLineRef.current.setData(valleyData)
    }
  }, [pattern.patternPoints])

  // Update projection line
  useEffect(() => {
    if (!projectionLineRef.current || !projectedTarget || !pattern.confirmation) return

    const projectionData = [
      {
        time: pattern.patternPoints.neckline[1].time as UTCTimestamp,
        value: projectedTarget
      },
      {
        time: (pattern.patternPoints.neckline[1].time + 86400 * 30) as UTCTimestamp, // 30 days ahead
        value: projectedTarget
      }
    ]

    projectionLineRef.current.setData(projectionData)
  }, [projectedTarget, pattern.confirmation, pattern.patternPoints.neckline])

  // Pattern info display
  const patternInfo = useMemo(() => {
    const quality = isValidPattern ? 'Valid' : 'Invalid'
    const type = pattern.type === 'double-top' ? 'Double Top (Bearish)' : 'Double Bottom (Bullish)'
    const confirmation = pattern.confirmation ? 'Confirmed' : 'Pending'
    const tolerance = `${(pattern.tolerance * 100).toFixed(1)}%`
    
    // Calculate peak similarity
    const priceDifference = Math.abs(pattern.patternPoints.firstPeak.price - pattern.patternPoints.secondPeak.price)
    const averagePrice = (pattern.patternPoints.firstPeak.price + pattern.patternPoints.secondPeak.price) / 2
    const similarity = `${(100 - (priceDifference / averagePrice) * 100).toFixed(1)}%`
    
    return {
      type,
      quality,
      confirmation,
      target: projectedTarget?.toFixed(2) || 'N/A',
      tolerance,
      similarity
    }
  }, [isValidPattern, pattern.type, pattern.confirmation, projectedTarget, pattern.tolerance, pattern.patternPoints])

  // Component doesn't render DOM elements, pattern is drawn using chart series
  return null
}

export default DoubleTopBottom

// Helper function to create a new Double Top/Bottom pattern
export const createDoubleTopBottomPattern = (
  id: string,
  type: 'double-top' | 'double-bottom',
  firstPeak: PatternPoint,
  valley: PatternPoint,
  secondPeak: PatternPoint,
  tolerance: number = 0.03
): DoubleTopBottomPattern => {
  // Create neckline - for double top, it connects valleys; for double bottom, it connects peaks
  const necklinePrice = type === 'double-top' ? 
    Math.min(firstPeak.price, secondPeak.price) : // Connect the lower points
    Math.max(firstPeak.price, secondPeak.price)   // Connect the higher points
  
  const neckline: [PatternPoint, PatternPoint] = [
    { 
      time: firstPeak.time, 
      price: necklinePrice, 
      role: 'neckline', 
      significance: 0.8 
    },
    { 
      time: secondPeak.time, 
      price: necklinePrice, 
      role: 'neckline', 
      significance: 0.8 
    }
  ]

  const patternColor = type === 'double-top' ? '#FF9800' : '#4CAF50'

  return {
    id,
    type,
    points: [firstPeak, valley, secondPeak].map(p => ({ time: p.time, price: p.price })),
    style: {
      lineColor: patternColor,
      lineWidth: 2,
      lineStyle: 'solid',
      textColor: '#E0E3EB',
      fontSize: 12
    },
    isSelected: false,
    isVisible: true,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    zIndex: 10,
    patternPoints: {
      firstPeak: { ...firstPeak, role: type === 'double-top' ? 'resistance' : 'support', significance: 0.9 },
      secondPeak: { ...secondPeak, role: type === 'double-top' ? 'resistance' : 'support', significance: 0.9 },
      valley: { ...valley, role: type === 'double-top' ? 'support' : 'resistance', significance: 0.7 },
      neckline
    },
    tolerance,
    confirmation: false
  }
} 